<template>
  <div class="input-group-container">
    <label v-if="label" :for="name">
      {{ label }}
      <template v-if="tooltip">
        <img v-if="currentTheme === 'light'"
             v-tooltip="{ content: tooltip }"
             class="info-icon"
             :src="icInfoLT"
             alt="copyIcon">
        <img v-else
             v-tooltip="{ content: tooltip }"
             class="info-icon"
             :src="icInfoDT"
             alt="copyIcon">
      </template>
    </label>
    <input v-if="type === 'password' || type === 'input'"
           :id="id"
           :ref="refName"
           v-model="model"
           :class="['input-item', { focused: focused, userInfoText: !isEmpty, passwordTypeIconToggle: isInputPasswordType }]"
           :name="name"
           :required="required"
           :placeholder="placeholder"
           type="text"
           autocomplete="off"
           @focus="onFocus"
           @blur="outFocus"
           @keyup="change"
           @paste="change">
    <textarea v-if="type === 'textarea'"
              :id="id"
              :ref="refName"
              v-model="model"
              :class="['input-item', { focused: focused, userInfoText: !isEmpty }]"
              :name="name"
              :required="required"
              :placeholder="placeholder"
              @focus="onFocus"
              @blur="outFocus"
              @keyup="change"
              @paste="change">
    </textarea>
    <div v-if="type === 'radio'">
      <label class="radio">
        <input :id="id"
               type="radio"
               :checked="radioValue === selected"
               :radioValue="radioValue"
               :value="radioValue"
               class="radio-input"
               @change="radioSelected(radioValue)">
        <span class="radio-selector"></span>
        <span class="radio-label">{{ radioLabel }}</span>
      </label>
    </div>
    <span v-if="isInputPasswordType"
          :key="id"
          class="ihub-password-group-visible-toggle"
          @click="() => togglePasswordVisibility()">
      <img v-if="currentTheme === 'dark' && isPasswordVisible" :src="passwordVisibleIconDT" alt="visiblePassword">
      <img v-if="currentTheme === 'dark' && !isPasswordVisible" :src="passwordHiddenIconDT" alt="hiddenPassword">
      <img v-if="currentTheme === 'light' && isPasswordVisible" :src="passwordVisibleIconDT" alt="visiblePassword">
      <img v-if="currentTheme === 'light' && !isPasswordVisible" :src="passwordHiddenIconLT" alt="hiddenPassword">
    </span>
  </div>
</template>

<script>
  import { debounce, get } from 'lodash';
  import { currentThemeMixin } from '@/mixins';
  import icInfoDT from '@/components/common/assets/ic_info/ic_info_DT.svg';
  import icInfoLT from '@/components/common/assets/ic_info/ic_info_LT.svg';
  import passwordHiddenIconDT from './../../../assets/img/eye_hidden.png';
  import passwordVisibleIconDT from './../../../assets/img/eye_visible.png';
  import passwordHiddenIconLT from './../../../assets/img/eye_hiddenLT.png';
  import passwordVisibleIconLT from './../../../assets/img/eye_visibleLT.png';

  export default {
    name: 'ihu-input-group',
    mixins: [currentThemeMixin],
    props: {
      type: {
        type: String,
        default: 'input',
      },
      label: {
        type: String,
        required: false,
        default: '',
      },
      name: {
        type: String,
        required: true,
      },
      id: {
        type: String,
        required: true,
      },
      required: {
        type: Boolean,
        required: false,
        default: false,
      },
      placeholder: {
        type: String,
        required: false,
        default: '',
      },
      value: {
        type: String,
        default: '',
      },
      radioLabel: {
        type: String,
        default: '',
      },
      radioValue: {
        type: String,
        default: '',
      },
      selected: {
        type: String,
        required: true,
      },
      tooltip: {
        type: String,
        default: '',
      },
    },
    data() {
      const isInputPasswordType = false;
      const isPasswordVisible = false;
      return {
        isPasswordVisible,
        isInputPasswordType,
        passwordHiddenIconDT,
        passwordVisibleIconDT,
        passwordHiddenIconLT,
        passwordVisibleIconLT,
        icInfoDT,
        icInfoLT,
        model: this.value,
        focused: false,
      };
    },
    computed: {
      refName() {
        return `inputField${this.id}`;
      },
      isEmpty() {
        return !this.model.length;
      },
    },
    mounted() {
      if (this.type === 'password') {
        const inputElement = document.getElementById(this.id);
        this.isInputPasswordType = true;
        inputElement.setAttribute('TYPE', 'password');
      }
    },
    methods: {
      onFocus() {
        this.focused = true;
        this.$emit('fieldFocus');
      },
      outFocus() {
        this.focused = false;
        this.$emit('fieldBlur');
      },
      sendChanges() {
        const text = get(this.$refs, `${this.refName}.value`, '');
        this.$emit('change', text);
      },
      change: debounce(function changeInput() {
        this.sendChanges();
      }, 150),
      radioSelected(value) {
        this.$emit('radioSelected', value);
      },
      togglePasswordVisibility() {
        this.isPasswordVisible = !this.isPasswordVisible;
        const inputElement = document.getElementById(this.id);
        if (!this.isPasswordVisible) {
          inputElement.setAttribute('TYPE', 'password');
        } else {
          inputElement.setAttribute('TYPE', 'text');
        }
      },
    },
  };
</script>

<style scoped lang="scss">
  .input-group-container {
    display: flex;
    flex-direction: column;

    label {
      display: flex;
      align-items: center;
      float: left;
      height: 14px;
      margin-left: 3px;
      font-family: Roboto;
      font-style: normal;
      font-weight: normal;
      font-size: 11.67px;
      line-height: 120%;
      text-align: justify;
      @include themify {
        color: getColor('medalliaSelfSetupItem', 'ihu');
      }

      /* Inside Auto Layout */

      flex: none;
      order: 0;
      flex-grow: 0;
      margin-bottom: 13px;
    }

    .info-icon {
      margin-left: 12px;
      height: 14px;
    }

    ::placeholder {
      @include themify {
        color: getColor('medalliaInputPlaceholder', 'ihu');
      }
    }

    .input-item {
      margin-bottom: 34px;
      display: flex;
      align-items: center;
      box-sizing: border-box;
      width: 100%;
      min-height: 38px;

      padding-left: 12px;
      border-radius: 3px;

      order: 0;
      align-self: stretch;

      font-family: Roboto;
      font-weight: normal;
      font-size: 14px;
      line-height: 150%;

      border: 1px solid rgba(140, 172, 255, 0.3);

      @include themify {
        background: getColor('medalliaSelfSetupItemInfoBackground', 'ihu');
        color: getColor('medalliaSelfSetupItemInfo', 'ihu');
        border-color: getColor('medalliaSelfSetupItemInfoBorderColor', 'ihu');
      }
    }

    textarea {
      resize: vertical;
      height: 110px;
    }

    .focused {
      font-style: italic;
      border: 3px solid rgba(140, 172, 255, 0.3);
    }

    .userInfoText {
      @include themify {
        background: getColor('medalliaSelfSetupItemInfoBackground', 'ihu');
        color: getColor('ihuInputGroupUserInfoText', 'ihu');
      }
    }

    //Input radio type styling
    .radio {
      margin-bottom: 34px;
      margin-right: 20px;
      display: inline-block;
      cursor: pointer;
      line-height: inherit;
      .radio-input {
        display: none;
      }
      .radio-selector {
        position: absolute;
        border-radius: 50%;
        height: 15px;
        width: 15px;
        border: 3px solid;
        @include themify {
          border-color: getColor('ihuInputGroupRadioUnselectedColor', 'ihu');
        }
      }
      .radio-input:checked ~ .radio-selector{
        height: 9px;
        width: 9px;
        border-radius: 50%;
        background-clip: content-box;
        padding: 3px;
        border: 3px solid;
        @include themify {
          background-color: getColor('ihuInputGroupRadioSelectedColor', 'ihu');
          border-color: getColor('ihuInputGroupRadioUnselectedColor', 'ihu');
        }
      }

      .radio-label {
        position: relative;
        padding-left: 35px;
        font-style: normal;
        font-weight: 700;
        font-size: 16px;
        line-height: 133%;
        display: flex;
        align-items: center;
      }
    }

    //Input password type styling
    /deep/ .passwordTypeIconToggle {
      padding-right: 50px;
    }

    .ihub-password-group-visible-toggle {
      cursor: pointer;
      position: relative;
      top: -69px;
      width: 50px;
      right: -90%;
    }
  }
</style>
